import { useAuth } from "@/contexts/AuthContext";
import { Navigate, useLocation } from "react-router-dom";

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: ("buyer" | "seller" | "admin")[];
  /**
   * If true (default), users must have kyc_status === 'verified' to access.
   * Set to false on the KYC page itself and other "always allowed" pages.
   */
  requireKyc?: boolean;
}

const ProtectedRoute = ({ children, allowedRoles, requireKyc = true }: ProtectedRouteProps) => {
  const { user, profile, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  // Role check
  if (allowedRoles && profile && !allowedRoles.includes(profile.role)) {
    const redirectPath = profile.role === "admin" ? "/admin/dashboard" : `/dashboard/${profile.role}`;
    return <Navigate to={redirectPath} replace />;
  }

  // Global KYC gate: buyers and sellers must complete identity verification before
  // accessing any protected feature. Admins are exempt. The /kyc page itself is exempt.
  if (
    requireKyc &&
    profile &&
    profile.role !== "admin" &&
    profile.kyc_status !== "verified" &&
    location.pathname !== "/kyc"
  ) {
    return <Navigate to="/kyc" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
